import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { workOrdersAPI, machinesAPI } from '../../services/api';
import { generateNativePdf, printStyles, CompanyLetterhead, PdfPreviewModal } from './shared';

export function WorkOrderPrintView() {
  const { woId } = useParams();
  const [workOrder, setWorkOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await workOrdersAPI.get(woId);
        setWorkOrder(res.data);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [woId]);

  useEffect(() => {
    if (workOrder && !loading) {
      setTimeout(() => window.print(), 500);
    }
  }, [workOrder, loading]);

  // Open preview modal
  const handlePreviewClick = () => {
    setShowPreview(true);
  };

  // Download PDF handler - A4 Portrait for Work Orders
  const handleDownloadPdf = async () => {
    if (!containerRef.current || generatingPdf) return;
    
    setGeneratingPdf(true);
    try {
      const filename = `WorkOrder_${workOrder.wo_number}_${new Date().toISOString().split('T')[0]}.pdf`;
      await generateNativePdf(
        containerRef.current, 
        filename, 
        'portrait',  // Portrait for Work Orders
        () => setGeneratingPdf(true),
        () => {
          setGeneratingPdf(false);
          setShowPreview(false);
        }
      );
    } catch (error) {
      alert('Failed to generate PDF. Please try again or use Print function.');
      setGeneratingPdf(false);
    }
  };

  if (loading) return <div className="p-8 text-center">Loading...</div>;
  if (!workOrder) return <div className="p-8 text-center">Work Order not found</div>;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <>
      {/* PDF Preview Modal - Uses real PDF blob for 100% accurate preview */}
      <PdfPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        onDownload={handleDownloadPdf}
        documentTitle="Work Order"
        documentNumber={workOrder.wo_number}
        orientation="portrait"
        sourceElement={containerRef.current}
        filename={`WorkOrder_${workOrder.wo_number}_${new Date().toISOString().split('T')[0]}.pdf`}
      />

      <div className="print-container" ref={containerRef} style={{ maxWidth: '210mm', margin: '0 auto', padding: '15px', fontFamily: 'Arial, sans-serif' }}>
        <style>{printStyles}</style>
        <style>{`
          @media print {
            @page { size: A4; margin: 10mm; }
            body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          }
          .traveler-table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          .traveler-table th, .traveler-table td { border: 1px solid #333; padding: 6px 8px; text-align: left; }
          .traveler-table th { background: #f0f0f0; font-weight: bold; }
          .operator-log { margin-top: 20px; }
          .operator-log th { background: #e0e0e0; }
          .operator-log td { height: 30px; }
          .qr-section { position: absolute; top: 15px; right: 15px; }
        `}</style>

        {/* Preview & Download PDF Button - Hidden from print */}
        <button 
          className="download-pdf-btn no-print" 
          onClick={handlePreviewClick}
          disabled={generatingPdf}
          data-testid="workorder-preview-pdf-btn"
        >
          {generatingPdf ? (
            <>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                <path d="M12 2v4m0 12v4m-8-10h4m12 0h4m-3.5-6.5l-2.83 2.83m-9.34 9.34l-2.83 2.83m0-12.17l2.83 2.83m9.34 9.34l2.83 2.83"/>
              </svg>
              Generating...
            </>
          ) : (
            <>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
              Preview &amp; Download PDF
            </>
          )}
        </button>

        {/* Header with QR Code */}
      <div style={{ position: 'relative', borderBottom: '3px solid #1e40af', paddingBottom: '10px', marginBottom: '15px' }}>
        {/* QR Code - Top Right */}
        <div className="qr-section no-print" style={{ zIndex: 1 }}>
          <QRCodeSVG value={workOrder.wo_number} size={80} level="M" />
          <div style={{ textAlign: 'center', fontSize: '8px', marginTop: '2px' }}>{workOrder.wo_number}</div>
        </div>

        {/* Company Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <img src="/logo.png" alt="Logo" style={{ height: '50px' }} onError={(e) => e.target.style.display = 'none'} />
          <div>
            <h1 style={{ margin: 0, fontSize: '18px', color: '#1e40af', fontWeight: 'bold' }}>SINGHANIA INTERNATIONAL LIMITED</h1>
            <div style={{ fontSize: '9px', color: '#666' }}>
              C-25, Phase-1, Focal Point, Dhandari Kalan, Ludhiana - 141010, Punjab, India
            </div>
          </div>
        </div>
        <div style={{ fontSize: '8px', color: '#059669', marginTop: '5px' }}>
          An ISO 9001, ISO 14001, ISO 45001 and IATF 16949 certified company
        </div>
      </div>

      {/* Document Title */}
      <div style={{ textAlign: 'center', marginBottom: '15px' }}>
        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', textTransform: 'uppercase' }}>WORK ORDER / TRAVELER</h2>
        <div style={{ fontSize: '14px', fontWeight: 'bold', marginTop: '5px' }}>{workOrder.wo_number}</div>
      </div>

      {/* Work Order Details */}
      <table className="traveler-table">
        <tbody>
          <tr>
            <th style={{ width: '20%' }}>WO Number</th>
            <td style={{ width: '30%', fontWeight: 'bold', fontSize: '14px' }}>{workOrder.wo_number}</td>
            <th style={{ width: '20%' }}>Order Ref</th>
            <td style={{ width: '30%' }}>{workOrder.order_number || '-'}</td>
          </tr>
          <tr>
            <th>Part Number</th>
            <td style={{ fontWeight: 'bold' }}>{workOrder.part_internal_no}</td>
            <th>Customer Code</th>
            <td style={{ fontFamily: 'monospace' }}>{workOrder.customer_code || '-'}</td>
          </tr>
          <tr>
            <th>Description</th>
            <td colSpan="3">{workOrder.part_description || '-'}</td>
          </tr>
          <tr>
            <th>Target Quantity</th>
            <td style={{ fontWeight: 'bold', fontSize: '14px' }}>{workOrder.qty_target?.toLocaleString()} pcs</td>
            <th>Assigned Machine</th>
            <td>{workOrder.machine_name || 'Not Assigned'}</td>
          </tr>
          <tr>
            <th>Planned Start</th>
            <td>{formatDate(workOrder.planned_start_date)}</td>
            <th>Planned End</th>
            <td>{formatDate(workOrder.planned_end_date)}</td>
          </tr>
        </tbody>
      </table>

      {/* Technical Specifications */}
      <h3 style={{ marginTop: '20px', marginBottom: '10px', fontSize: '14px', borderBottom: '1px solid #333', paddingBottom: '5px' }}>
        MATERIAL & TECHNICAL SPECIFICATIONS
      </h3>
      <table className="traveler-table">
        <tbody>
          <tr>
            <th style={{ width: '20%' }}>RM Grade</th>
            <td style={{ width: '30%' }}>{workOrder.rm_grade || '-'}</td>
            <th style={{ width: '20%' }}>RM Size</th>
            <td style={{ width: '30%' }}>{workOrder.rm_size || '-'}</td>
          </tr>
          <tr>
            <th>Wire Size (After Drawing)</th>
            <td>{workOrder.wire_size || '-'}</td>
            <th>RM Qty Required</th>
            <td>{workOrder.rm_qty_required ? `${workOrder.rm_qty_required} Kg` : '-'}</td>
          </tr>
          <tr>
            <th>Supply Condition</th>
            <td>{workOrder.supply_condition || '-'}</td>
            <th>Header Tool Status</th>
            <td>{workOrder.header_tool_status || '-'}</td>
          </tr>
          <tr>
            <th>Heat Treatment</th>
            <td>{workOrder.heat_treatment || '-'}</td>
            <th>Surface Finish</th>
            <td>{workOrder.surface_finish || '-'}</td>
          </tr>
        </tbody>
      </table>

      {/* Production Notes */}
      {workOrder.production_notes && (
        <div style={{ marginTop: '15px', padding: '10px', background: '#fff9e6', border: '1px solid #f0d000' }}>
          <strong>Production Notes:</strong> {workOrder.production_notes}
        </div>
      )}

      {/* Operator Log Table */}
      <h3 style={{ marginTop: '25px', marginBottom: '10px', fontSize: '14px', borderBottom: '1px solid #333', paddingBottom: '5px' }}>
        OPERATOR LOG
      </h3>
      <table className="traveler-table operator-log">
        <thead>
          <tr>
            <th style={{ width: '12%' }}>Date</th>
            <th style={{ width: '12%' }}>Shift</th>
            <th style={{ width: '15%' }}>Operator</th>
            <th style={{ width: '12%' }}>Qty OK</th>
            <th style={{ width: '12%' }}>Qty Reject</th>
            <th style={{ width: '12%' }}>Machine</th>
            <th style={{ width: '25%' }}>Remarks</th>
          </tr>
        </thead>
        <tbody>
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <tr key={`row-${i}`}>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Quality Check Section */}
      <h3 style={{ marginTop: '25px', marginBottom: '10px', fontSize: '14px', borderBottom: '1px solid #333', paddingBottom: '5px' }}>
        QUALITY INSPECTION
      </h3>
      <table className="traveler-table">
        <thead>
          <tr>
            <th>Parameter</th>
            <th>Specification</th>
            <th>Actual</th>
            <th>Status</th>
            <th>Inspector</th>
          </tr>
        </thead>
        <tbody>
          {[1, 2, 3, 4].map((i) => (
            <tr key={`row-${i}`}>
              <td style={{ height: '25px' }}></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Footer */}
      <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'space-between', fontSize: '10px' }}>
        <div>
          <strong>Created:</strong> {formatDate(workOrder.created_at)}
          {workOrder.released_at && <span> | <strong>Released:</strong> {formatDate(workOrder.released_at)}</span>}
        </div>
        <div>
          <strong>Status:</strong> {workOrder.status?.toUpperCase()}
        </div>
      </div>
      <div style={{ marginTop: '10px', textAlign: 'center', fontSize: '9px', color: '#666' }}>
        For queries, contact Production Planning at info@singhaniainternational.com
      </div>
    </div>
    </>
  );
}

// ============== PLANNING SHEET PRINT VIEW ==============

